import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";

/**
 * Pin plans: the wholesale price list an operator publishes to its PIN
 * sub-operators -- radaccess /access/operator/{ns}/pinplans (feat/pin-plan,
 * owner 2026-09-19), RBAC resource `pinplan` (superadmin and Operator).
 *
 * One pin carries one subscriber, on any plan at or below `speedmbps`, for one
 * recharge of up to `termdays`. The parent says per plan whether the price
 * includes GST. Money is in PAISE throughout; the screen converts.
 *
 * 🔑 A sub-operator never reads its parent's list (it names the siblings);
 * it reads /pinplans/offered on its OWN namespace.
 */
export type PinPlanAudience = "all" | "named";

/**
 * radaccess 5af962b (owner 2026-09-28, wholesale-pin-distribution §15):
 * wholesale pins are SOLD to PIN sub-operators; access pins are issued by a
 * Network Operator to its own SITES -- no price, no GST, no SAC, never invoiced.
 */
export type PinPlanKind = "wholesale" | "access";

/**
 * The kind of plan an operator of this type publishes, or null when the type
 * is not known. 🚨 Unknown is NOT wholesale: on 2026-10-10 radaccess answered
 * Lotus's /kind with 404 (a NULL legal name), the console guessed wholesale,
 * and the form offered Lotus's retired PIN child instead of its new site.
 */
export function planKindForPublisher(operatorType: string | null): PinPlanKind | null {
  if (operatorType === "NETWORK_OPERATOR") return "access";
  return publishesWholesalePins(operatorType) ? "wholesale" : null;
}

/**
 * Only a licensed ISP or VNO sells wholesale pins (owner 2026-10-10; radaccess
 * PublishesWholesalePins): the licensed operator invoices the pins. A Reseller
 * or a PIN provider has no licence, so its pin invoice would be refused.
 */
export function publishesWholesalePins(operatorType: string | null): boolean {
  return operatorType === "ISP" || operatorType === "VNO";
}

/** The sub-operator kind that may hold pins of a plan kind (radaccess BuyerKindFor). */
export function buyerKindFor(kind: PinPlanKind): "pin" | "site" {
  return kind === "access" ? "site" : "pin";
}

export interface PinPlan {
  id: string;
  namespace: string;
  /** Absent from a radaccess older than 5af962b: read as wholesale. */
  kind?: PinPlanKind;
  name: string;
  speedmbps: number;
  termdays: number;
  pricepaise: number;
  priceincludesgst: boolean;
  gstratebp: number;
  sac: string;
  netpaise: number;
  gstpaise: number;
  grosspaise: number;
  audience: PinPlanAudience;
  members: string[];
  active: boolean;
  createdby?: string;
  updatedby?: string;
  createdat: string;
  updatedat: string;
}

export interface PinPlanInput {
  /** Sent on create AND update: radaccess refuses an update that changes it. */
  kind?: PinPlanKind;
  name: string;
  speedmbps: number;
  termdays: number;
  pricepaise: number;
  priceincludesgst: boolean;
  gstratebp: number;
  sac?: string;
  audience: PinPlanAudience;
  members?: string[];
  /** Update only; a new plan is always active. */
  active?: boolean;
}

/** What /offered answers: whether the caller is a sub-operator, and its offer. */
export interface PinOffer {
  suboperator: boolean;
  parent?: string;
  kind?: string;
  suspended: boolean;
  plans: PinPlan[];
  /** Wholesale plans of sellers that linked this operator as a pin buyer; each plan's namespace is its seller. */
  linkedplans: PinPlan[];
}

export const GST_RATES_BP = [0, 500, 1200, 1800, 2800] as const;

const base = (ns: string) => `/operator/${encodeURIComponent(ns)}/pinplans`;

export async function listPinPlans(identity: CallerIdentity, parent: string, signal?: AbortSignal): Promise<PinPlan[]> {
  const r = await callGateway<PinPlan[] | null>(identity, config().gatewayBase, base(parent), signal ? { signal } : {});
  return r.data ?? [];
}

export async function getPinOffer(identity: CallerIdentity, ns: string, signal?: AbortSignal): Promise<PinOffer> {
  const r = await callGateway<PinOffer | null>(identity, config().gatewayBase, `${base(ns)}/offered`, signal ? { signal } : {});
  return { suboperator: false, suspended: false, ...(r.data ?? {}), plans: r.data?.plans ?? [], linkedplans: r.data?.linkedplans ?? [] };
}

export async function createPinPlan(identity: CallerIdentity, parent: string, input: PinPlanInput): Promise<PinPlan> {
  const r = await callGateway<PinPlan>(identity, config().gatewayBase, base(parent), { method: "POST", body: cleanPinPlanInput(input, false) });
  return r.data;
}

export async function updatePinPlan(identity: CallerIdentity, parent: string, id: string, input: PinPlanInput): Promise<PinPlan> {
  const r = await callGateway<PinPlan>(identity, config().gatewayBase, `${base(parent)}/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: cleanPinPlanInput(input, true),
  });
  return r.data;
}

/**
 * The body sent. radaccess refuses unknown fields; members ride only on a named
 * plan, active only on an update.
 */
export function cleanPinPlanInput(i: PinPlanInput, update: boolean): PinPlanInput {
  const out: PinPlanInput = {
    name: i.name.trim().replace(/\s+/g, " "),
    speedmbps: i.speedmbps,
    termdays: i.termdays,
    pricepaise: i.pricepaise,
    priceincludesgst: i.priceincludesgst,
    gstratebp: i.gstratebp,
    audience: i.audience,
  };
  if (i.kind) out.kind = i.kind;
  if (i.kind === "access") {
    // ⛔ radaccess REFUSES a price, GST or SAC on an access plan; send none.
    out.pricepaise = 0;
    out.priceincludesgst = false;
    out.gstratebp = 0;
  } else if (i.sac?.trim()) out.sac = i.sac.trim();
  if (i.audience === "named") out.members = [...new Set((i.members ?? []).map((m) => m.trim().toLowerCase()).filter(Boolean))].sort();
  if (update && i.active !== undefined) out.active = i.active;
  return out;
}

/**
 * "100", "100.5", "1,00,000.25" → paise. null for anything that is not a
 * positive amount with at most two decimals -- never a float round-trip.
 */
export function paiseFromRupees(text: string): number | null {
  const t = text.replace(/[,\s₹]/g, "");
  const m = /^(\d{1,7})(?:\.(\d{1,2}))?$/.exec(t);
  if (!m) return null;
  const paise = Number(m[1]) * 100 + Number((m[2] ?? "").padEnd(2, "0"));
  return paise > 0 ? paise : null;
}

/** 10050 → "100.50", for putting a stored price back into the input box. */
export function rupeesText(paise: number): string {
  return `${Math.floor(paise / 100)}.${String(paise % 100).padStart(2, "0")}`;
}

/**
 * The per-pin split radaccess computes (model.PinSplit), so the form shows
 * what will be saved before it is: inclusive → net rounded half up and GST
 * the remainder; exclusive → GST rounded half up on top.
 */
export function pinSplit(pricePaise: number, includesGST: boolean, rateBP: number): { net: number; gst: number; gross: number } {
  if (includesGST) {
    const net = Math.floor((pricePaise * 20000 + (10000 + rateBP)) / (2 * (10000 + rateBP)));
    return { net, gst: pricePaise - net, gross: pricePaise };
  }
  const gst = Math.floor((pricePaise * rateBP * 2 + 10000) / 20000);
  return { net: pricePaise, gst, gross: pricePaise + gst };
}

export function gstLabel(bp: number): string {
  return `${bp / 100}%`;
}
